import React, { useState } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CollectionsIcon from '@mui/icons-material/Collections';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: '#0a0a0f',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(236, 72, 153, 0.3), transparent)'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto'
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(236, 72, 153, 0.1)',
  border: '1px solid rgba(236, 72, 153, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#f472b6',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 50%, #fb7185 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const MasonryGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridAutoRows: '200px',
  gap: '16px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(3, 1fr)'
  },
  '@media (max-width: 768px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoRows: '160px'
  }
});

const GalleryItem = styled(Box)(({ span, index }) => ({
  gridColumn: span === 2 ? 'span 2' : 'span 1',
  gridRow: span === 2 ? 'span 2' : 'span 1',
  borderRadius: '20px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.7) 100%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: 1
  },
  '&:hover': {
    '&::before': {
      opacity: 1
    },
    '& img': {
      transform: 'scale(1.1)'
    },
    '& .overlay-content': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  '@media (max-width: 768px)': {
    gridColumn: 'span 1',
    gridRow: 'span 1'
  }
}));

const GalleryImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
});

const OverlayContent = styled(Box)({
  position: 'absolute',
  bottom: '20px',
  left: '20px',
  right: '20px',
  zIndex: 2,
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'all 0.4s ease'
});

const ImageCaption = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: '#ffffff'
});

const LightboxModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const LightboxContent = styled(Box)({
  position: 'relative',
  maxWidth: '90vw',
  maxHeight: '90vh',
  outline: 'none'
});

const LightboxImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '90vh',
  objectFit: 'contain',
  borderRadius: '16px'
});

const LightboxClose = styled(IconButton)({
  position: 'absolute',
  top: '-50px',
  right: '0',
  color: '#ffffff',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)'
  }
});

const LightboxNav = styled(IconButton)({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#ffffff',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)'
  }
});

// Helper to extract URL from various photo object formats
const getPhotoUrl = (photo) => {
  if (!photo) return null;
  if (typeof photo === 'string') return photo;
  return photo.url || photo.googleUrl || photo.sourceUrl || photo.image || photo.imageUrl || photo.thumbnailUrl || null;
};

const PhotoGallery = ({ photos }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filter photos to only include those with valid URLs
  const validPhotos = (photos || []).filter(photo => getPhotoUrl(photo));
  
  if (validPhotos.length === 0) return null;

  const displayPhotos = validPhotos.slice(0, 8);

  const getSpan = (index) => {
    if (index === 0 || index === 3) return 2;
    return 1;
  };

  const handleImageClick = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? displayPhotos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === displayPhotos.length - 1 ? 0 : prev + 1));
  };

  return (
    <SectionContainer id="gallery">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <CollectionsIcon sx={{ fontSize: 16, color: '#f472b6' }} />
            <BadgeText>Gallery</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Our <GradientText>Portfolio</GradientText>
          </SectionTitle>
        </SectionHeader>

        <MasonryGrid>
          {displayPhotos.map((photo, index) => (
            <GalleryItem
              key={photo.id || index}
              span={getSpan(index)}
              index={index}
              onClick={() => handleImageClick(index)}
            >
              <GalleryImage src={getPhotoUrl(photo)} alt={photo.caption || photo.alt || photo.description || photo.name || `Gallery image ${index + 1}`} />
              <OverlayContent className="overlay-content">
                <ImageCaption>{photo.caption || `View Image`}</ImageCaption>
              </OverlayContent>
            </GalleryItem>
          ))}
        </MasonryGrid>
      </ContentWrapper>

      <LightboxModal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.95)' } }}
      >
        <LightboxContent>
          <LightboxClose onClick={() => setLightboxOpen(false)}>
            <CloseIcon />
          </LightboxClose>
          <LightboxNav sx={{ left: '-60px' }} onClick={handlePrev}>
            <ArrowBackIosNewIcon />
          </LightboxNav>
          <LightboxImage
            src={getPhotoUrl(displayPhotos[currentIndex])}
            alt={displayPhotos[currentIndex]?.caption || ''}
          />
          <LightboxNav sx={{ right: '-60px' }} onClick={handleNext}>
            <ArrowForwardIosIcon />
          </LightboxNav>
        </LightboxContent>
      </LightboxModal>
    </SectionContainer>
  );
};

export default PhotoGallery;
